import { useState } from 'react';
import { tcesToHeatmapColor, type GtpHeatmapModel } from '@/utils/buildGtpHeatmapData';
import { formatTcesScore } from '@/utils/formatGtpList';

const CLASS_LABEL_MAX = 36;

type GenesGtpHeatmapMatrixProps = {
  heatmap: GtpHeatmapModel;
};

type TooltipState = {
  gene: string;
  className: string;
  tces: number;
  left: number;
  top: number;
};

function truncateClassLabel(name: string): string {
  if (name.length <= CLASS_LABEL_MAX) return name;
  return `${name.slice(0, CLASS_LABEL_MAX - 1)}…`;
}

export function GenesGtpHeatmapMatrix({ heatmap }: GenesGtpHeatmapMatrixProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  return (
    <div className="gtp-heatmap__layout">
      <div className="gtp-heatmap__chart" data-testid="gtp-heatmap-chart">
        <div className="gtp-heatmap__ylabels">
          {heatmap.series.map(row => (
            <div key={row.id} className="gtp-heatmap__ylabel" title={row.id}>
              {truncateClassLabel(row.id)}
            </div>
          ))}
        </div>
        <div className="gtp-heatmap__columns">
          {heatmap.genes.map((gene, geneIndex) => (
            <div key={gene.id} className="gtp-heatmap__column">
              <div className="gtp-heatmap__column-track">
                {heatmap.series.map(row => {
                  const value = row.data[geneIndex]?.y ?? 0;
                  return (
                    <div
                      key={row.id}
                      className="gtp-heatmap__cell"
                      style={{
                        backgroundColor: tcesToHeatmapColor(value, heatmap.maxPositive, heatmap.minNegative),
                      }}
                      onMouseEnter={event => {
                        const rect = event.currentTarget.getBoundingClientRect();
                        setTooltip({
                          gene: gene.symbol,
                          className: row.id,
                          tces: value,
                          left: rect.left + rect.width / 2,
                          top: rect.top,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  );
                })}
              </div>
              <div className="gtp-heatmap__xlabel" title={gene.symbol}>
                {gene.symbol}
              </div>
            </div>
          ))}
        </div>
      </div>
      <aside className="gtp-heatmap__legend" aria-label="TCES legend">
        <span className="gtp-heatmap__legend-title">TCES</span>
        <span className="gtp-heatmap__legend-value">{heatmap.maxPositive.toFixed(1)}</span>
        <div className="gtp-heatmap__legend-bar" />
        <span className="gtp-heatmap__legend-value">{heatmap.minNegative.toFixed(1)}</span>
      </aside>
      {tooltip ? (
        <div
          className="gda-analytics-tooltip gtp-heatmap__tooltip"
          style={{ left: tooltip.left, top: tooltip.top }}
        >
          <strong>{tooltip.gene}</strong>
          <span>{tooltip.className}</span>
          <div>TCES: {formatTcesScore(tooltip.tces) || '0'}</div>
        </div>
      ) : null}
    </div>
  );
}
